// Leitura do prazo das tarefas. Um só lugar para Tarefas, Agenda e Dashboard.
//
// Como o prazo é gravado:
// - com horário (hasDueTime = true): o instante exato → vale a hora local;
// - dia todo, formato novo: meio-dia UTC do dia (mesmo dia no Brasil e em UTC);
// - dia todo, formato antigo manual: meia-noite UTC do dia → vale a data UTC;
// - dia todo, formato antigo automático (CRM): hora qualquer ("agora + N
//   dias") → vale a data local, que é o dia em que a tarefa foi pensada.

const DAY_MS = 86400000;
const pad = (n) => String(n).padStart(2, "0");

const isUtcMidnightOrNoon = (d) =>
  d.getUTCMinutes() === 0 &&
  d.getUTCSeconds() === 0 &&
  d.getUTCMilliseconds() === 0 &&
  (d.getUTCHours() === 0 || d.getUTCHours() === 12);

// dia da tarefa como Date local à meia-noite (ou null sem prazo)
export const taskDueDay = (task) => {
  if (!task?.dueDate) return null;
  const d = new Date(task.dueDate);
  if (Number.isNaN(d.getTime())) return null;
  if (!task.hasDueTime && isUtcMidnightOrNoon(d)) {
    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  }
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

// "YYYY-MM-DD" para campos de data
export const taskDueInput = (task) => {
  const day = taskDueDay(task);
  return day ? `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}` : "";
};

// "HH:mm" quando a tarefa tem horário; "" se for dia todo
export const taskDueTime = (task) => {
  if (!task?.dueDate || !task.hasDueTime) return "";
  const d = new Date(task.dueDate);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// dias entre hoje e o dia da tarefa (negativo = passou)
export const taskDaysUntil = (task, now = new Date()) => {
  const day = taskDueDay(task);
  if (!day) return null;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((day - today) / DAY_MS);
};

// atrasada: com horário, quando a hora passa; dia todo, no dia seguinte
export const isTaskOverdue = (task, now = new Date()) => {
  if (!task?.dueDate || task.completed) return false;
  if (task.hasDueTime) return new Date(task.dueDate).getTime() < now.getTime();
  const days = taskDaysUntil(task, now);
  return days !== null && days < 0;
};

// chave para ordenar por prazo (dia todo conta como fim do dia)
export const taskDueSortKey = (task) => {
  const day = taskDueDay(task);
  if (!day) return Infinity;
  if (task.hasDueTime) return new Date(task.dueDate).getTime();
  return day.getTime() + DAY_MS - 1;
};

// "26/10/2026" ou "26/10/2026 · 15:00"
export const formatTaskDue = (task, { year = true } = {}) => {
  const day = taskDueDay(task);
  if (!day) return "";
  const date = `${pad(day.getDate())}/${pad(day.getMonth() + 1)}${year ? `/${day.getFullYear()}` : ""}`;
  const time = taskDueTime(task);
  return time ? `${date} · ${time}` : date;
};

// valor para a API: só data = dia todo; data + hora = instante (ISO)
export const buildDuePayload = (date, time) => {
  if (!date) return null;
  if (!time) return date;
  const [y, m, d] = date.split("-").map(Number);
  const [hh, mm] = time.split(":").map(Number);
  return new Date(y, m - 1, d, hh, mm, 0, 0).toISOString();
};

// Lembrete (só tarefa com horário): minutos antes do horário
export const REMINDER_OPTIONS = [0, 5, 15, 30, 60, 1440];
export const DEFAULT_REMINDER = 15;

// "na hora", "15 min antes", "1 h antes", "1 dia antes"
export const reminderLabel = (minutes) => {
  const m = Number(minutes);
  if (!m) return "Na hora";
  if (m % 1440 === 0) return `${m / 1440} ${m === 1440 ? "dia" : "dias"} antes`;
  if (m % 60 === 0) return `${m / 60} h antes`;
  return `${m} min antes`;
};
